import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import {
  useUpdateMarketZoneBreakdown,
  UpdateMarketZoneBreakdownSchema,
  MarketZoneBreakdown,
} from "@/lib/api/risk/marketZoneBreakthrough";
import { MarketZoneBreakdownForm } from "./MarketZoneBreakdownForm";
import { findFilteredDifferences, cleanObject } from "@/lib/prelude";

interface UpdateMarketZoneBreakdownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData: MarketZoneBreakdown;
}

interface MarketZoneBreakdownFormData {
  customer_id: string;
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  month_one?: string;
  month_two?: string;
  month_three?: string;
  month_four?: string;
  month_five?: string;
  month_six?: string;
  month_seven?: string;
  month_eight?: string;
  month_nine?: string;
  month_ten?: string;
  month_eleven?: string;
  month_twelve?: string;
  total: string;
  amount: string;
  note?: string;
}

export function UpdateMarketZoneBreakdownDialog({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}: UpdateMarketZoneBreakdownDialogProps) {
  const { toast } = useToast();
  const updateMarketZoneBreakdown = useUpdateMarketZoneBreakdown();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = async (data: MarketZoneBreakdownFormData) => {
    setIsUpdating(true);
    try {
      // Prepare current form data for comparison
      const currentData = cleanObject({
        customer_id: data.customer_id,
        market_zone_id: data.market_zone_id,
        commodity: data.commodity,
        crop_year: data.crop_year,
        month_one: data.month_one?.toString(),
        month_two: data.month_two?.toString(),
        month_three: data.month_three?.toString(),
        month_four: data.month_four?.toString(),
        month_five: data.month_five?.toString(),
        month_six: data.month_six?.toString(),
        month_seven: data.month_seven?.toString(),
        month_eight: data.month_eight?.toString(),
        month_nine: data.month_nine?.toString(),
        month_ten: data.month_ten?.toString(),
        month_eleven: data.month_eleven?.toString(),
        month_twelve: data.month_twelve?.toString(),
        total: data.total?.toString(),
        amount: data.amount?.toString(),
        note: data.note,
      }, { removeEmptyStrings: true });

      // Find all differences: new, removed, and changed fields
      const updatedFields = findFilteredDifferences(
        currentData,
        initialData,
        { new: true, changed: true, ignoreColumns: ["id", "created_at", "updated_at", "Customer", "MarketZone"] }
      );

      const removedFields = findFilteredDifferences(
        currentData,
        initialData,
        { removed: true, ignoreColumns: ["id", "created_at", "updated_at", "Customer", "MarketZone"] }
      );

      console.warn("trying to update", currentData, initialData, updatedFields, removedFields)
      // If no changes, don't send any data
      if (!updatedFields.length && !removedFields.length) {
        toast({
          title: "Updated",
          description: "No changes detected to update.",
        });
        setIsUpdating(false);
        return;
      }

      // Build update data with only the changed fields
      const updateData: any = {};

      // Map of form data to API field names
      const formDataMap: Record<string, any> = {
        customer_id: data.customer_id,
        market_zone_id: data.market_zone_id,
        commodity: data.commodity,
        crop_year: data.crop_year,
        month_one: data.month_one?.toString(),
        month_two: data.month_two?.toString(),
        month_three: data.month_three?.toString(),
        month_four: data.month_four?.toString(),
        month_five: data.month_five?.toString(),
        month_six: data.month_six?.toString(),
        month_seven: data.month_seven?.toString(),
        month_eight: data.month_eight?.toString(),
        month_nine: data.month_nine?.toString(),
        month_ten: data.month_ten?.toString(),
        month_eleven: data.month_eleven?.toString(),
        month_twelve: data.month_twelve?.toString(),
        total: data.total?.toString(),
        amount: data.amount?.toString(),
        note: data.note,
      };

      // Only include fields that have changed
      [...removedFields, ...updatedFields].forEach(field => {
        if (removedFields.includes(field)) {
          // For removed fields, set to null (except required fields)
          const requiredFields = ['customer_id', 'market_zone_id', 'commodity', 'crop_year', 'amount'];
          if (!requiredFields.includes(field)) {
            updateData[field] = null;
          }
        } else {
          // For new or changed fields, use the current value
          updateData[field] = formDataMap[field] || undefined;
        }
      });

      // Validate the data
      const validationResult = UpdateMarketZoneBreakdownSchema.safeParse(updateData);

      if (!validationResult.success) {
        const errors = validationResult.error.errors
          .map((err) => `${err.path.join(".")}: ${err.message}`)
          .join("\n");
        console.error("Failed to validate update breakdown", updateData, errors);

        toast({
          title: "Invalid Data",
          description: "Please check your input and try again.",
          variant: "destructive",
        });
        return;
      }

      const result = await updateMarketZoneBreakdown(initialData.id, validationResult.data);

      if (!result.success) {
        throw result;
      }

      toast({
        title: "Success",
        description: "Market zone breakdown updated successfully",
      });
      onSuccess();
    } catch (error) {
      console.error("Failed to update market zone breakdown", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Market Zone Breakdown</DialogTitle>
          <DialogDescription>
          </DialogDescription>
        </DialogHeader>

        <MarketZoneBreakdownForm
          mode="edit"
          onSubmit={handleSubmit}
          onCancel={handleClose}
          showCancelButton={true}
          isSubmitting={isUpdating}
          initialData={initialData}
        />
      </DialogContent>
    </Dialog>
  );
}
